import {
  addMonths,
  differenceInCalendarMonths,
  isAfter,
  addDays,
  isBefore,
  isEqual
} from "date-fns";
import {
  workDaysInMonth,
  differenceInWorkDays,
  getExpansionDateKey,
  getFirstWorkdayOfMonth,
  getFirstWorkdayOfWeek,
  getLastWorkdayOfMonth,
  getLastWorkdayOfWeek,
} from "@repo/structure";
import { CELL_WIDTHS } from "@utils/settings/editorCellWidths";

export const getTimelineMonthWidth = (startDate, expandedMonths, endDate, cutLastDayWidth=false) => {
  const monthExpansion = expandedMonths?.get(
    getExpansionDateKey(getFirstWorkdayOfMonth(startDate), true),
  );

  if (!monthExpansion) return CELL_WIDTHS.VALUE.SMALL;

  const firstWeek = getFirstWorkdayOfWeek(startDate, { weekStartsOn: 1 });
  const monthEnd =
    endDate && endDate < getLastWorkdayOfMonth(startDate)
      ? endDate
      : getLastWorkdayOfMonth(startDate);
  let monthWidth = 0;

  for (
    let weekStart = firstWeek;
    !isAfter(weekStart, monthEnd);
    weekStart = getFirstWorkdayOfWeek(addDays(weekStart, 7))
  ) {
    const weekExpansion = monthExpansion.weeks?.get(
      getExpansionDateKey(weekStart, false),
    );

    if (!weekExpansion) {
      monthWidth += CELL_WIDTHS.VALUE.SMALL;
    } else {
      const days = Array.from({ length: 5 }, (_, dayIndex) =>
        addDays(weekStart, dayIndex),
      ).filter((day) => {
        if (cutLastDayWidth && isEqual(day, endDate)) return false;
        // if (isEqual(startDate, endDate) && isEqual(day, endDate)) return false;
        return !isBefore(day, startDate) && !isAfter(day, monthEnd);
      });

      monthWidth += days.length * CELL_WIDTHS.VALUE.SMALL;
    }
  }

  return monthWidth;
};

export const getTimelineCellPeriods = (monthValue, expandedMonths) => {
  const monthExpansion = expandedMonths?.get(
    getExpansionDateKey(monthValue.startDate, true),
  );

  if (!monthExpansion)
    return [ { ...monthValue, type: "month" } ];

  return monthValue.weeks.flatMap((weekValue) => {
    const weekExpansion = monthExpansion.weeks?.get(
      getExpansionDateKey(weekValue.startDate, false),
    );

    if (!weekExpansion) {
      return [ { ...weekValue, type: "week", monthValue: monthValue.value } ];
    }

    return (weekValue.days ?? weekValue.distribution).map((dayValue) => ({
      ...dayValue,
      type: "day",
      weekValue: weekValue.value,
      monthValue: monthValue.value,
    }));
  });
};


const getTimelineSegmentOffset = (date, fromEnd, isMonth, includeLastDay) => {
  const earlierDate = isMonth ? getFirstWorkdayOfMonth(date) : getFirstWorkdayOfWeek(date);
  const laterDate = isMonth ? getLastWorkdayOfMonth(date) : getLastWorkdayOfWeek(date);

  const base = isMonth
    ? workDaysInMonth(date)
    : differenceInWorkDays(laterDate, earlierDate);

  const elapsedDays = fromEnd
    ? base - differenceInWorkDays(date, earlierDate) - (includeLastDay ? 1 : 0)
    : differenceInWorkDays(date, earlierDate);

  return Math.round(
    Math.max(0, Math.min(1, elapsedDays / base)) * CELL_WIDTHS.VALUE.SMALL
  );
};

export const getTimelineSegmentWidth = ({
  startDate,
  endDate,
  expandedMonths,
  includeLastDay,
  isFirstTimelineMonth,
}) => {
  if (!startDate || !endDate || endDate < startDate || (!includeLastDay && !isFirstTimelineMonth && isEqual(startDate, endDate)))
    return { width: 0, unmodifiedWidth: 0, offsetLeft: 0, offsetRight: 0 };

  const startMonth = getFirstWorkdayOfMonth(startDate);
  const endMonth = getFirstWorkdayOfMonth(endDate);
  const monthCount = differenceInCalendarMonths(endMonth, startMonth) + 1;
  let width = 0;

  for (let monthIndex = 0; monthIndex < monthCount; monthIndex++)
    width += getTimelineMonthWidth(
      monthIndex ? addMonths(startMonth, monthIndex) : startDate,
      expandedMonths,
      endDate,
      !includeLastDay,
    );

  const normalizedStartDate = isFirstTimelineMonth ? getFirstWorkdayOfMonth(startDate) : startDate;

  let offsetLeft = 0, offsetRight = 0;

  if (!isEqual(normalizedStartDate, getFirstWorkdayOfMonth(normalizedStartDate))) {
    const isFrontMonthExpanded = expandedMonths?.get(
      getExpansionDateKey(startMonth, true),
    );
    if (
      !isFrontMonthExpanded?.weeks?.get(
        getExpansionDateKey(getFirstWorkdayOfWeek(normalizedStartDate), false),
      )
    )
      offsetLeft = getTimelineSegmentOffset(
        normalizedStartDate,
        false,
        !isFrontMonthExpanded,
      );
  }

  if (!isEqual(endDate, getLastWorkdayOfMonth(endDate))) {
    const isEndMonthExpanded = expandedMonths?.get(
      getExpansionDateKey(endMonth, true),
    );
    if (
      !isEndMonthExpanded?.weeks?.get(
        getExpansionDateKey(getFirstWorkdayOfWeek(endDate), false),
      )
    )
      offsetRight = getTimelineSegmentOffset(endDate, true, !isEndMonthExpanded, includeLastDay);
  }

  const unmodifiedWidth = width;
  width = Math.round(width - offsetLeft - offsetRight);

  return { width, unmodifiedWidth, offsetLeft, offsetRight };
};

export const getUtilizationWeightColor = (weight) => {
  if (weight === 0) return "#DE0909";
  if (weight > 0 && weight < 1) return "#F28121";
  if (weight > 1) return "#FEBC2F";
  return undefined;
};
